import type { SizeChartAttributeType, SizeChartResultType } from 'src/types/decorationSpecs';

/**
 * Resolves apparel body Width/Length data from the same already-loaded product data the SKU
 * picker (`VariablesV3` → `GenericSelector`) uses for its own attribute pickers.
 *
 * Two kinds of consumer:
 * - `buildSizeChartFromProduct` — the Size Chart table shown in the decoration specs panel.
 * - `resolveBodyWidthInches`/`resolveBodyLengthInches` — a single resolved-to-inches value for the
 *   currently selected size, for the auto-placement geometry math (see
 *   ai-config/user-stories/apparel-positioning-and-auto-placement/summary.md's Phase B).
 *
 * Both are name-pattern-matched against specific candidate property names (`Width`, `Body Width`,
 * `Length (A)`, etc. — see `BODY_WIDTH_PATTERNS`/`BODY_LENGTH_PATTERNS`) rather than any generic
 * "numeric property that varies by size" heuristic — a product's attribute model can contain
 * plenty of other numeric, unit-bearing, size-varying properties that aren't body measurements
 * (e.g. per-size shipping weight), and this module needs to identify *specifically* Width/Length,
 * not "anything that looks size-chart-shaped".
 */

/** One `values[]` entry inside a condition or a resolved result — only the field this module
 * reads is declared. */
type LiteralValueType = {
    value?: string;
};

/** One condition inside a property assignment, e.g. `{ attribute: 'Size', operator: 'in',
 * values: [{ value: 'M' }] }`. */
type ConditionType = {
    attribute: string;
    operator: 'in' | 'notIn' | string;
    values: LiteralValueType[];
};

/** One conditional branch of a property's value, e.g. "when Size is M, this is 20". */
type PropertyAssignmentType = {
    conditions: ConditionType[];
    result: LiteralValueType;
};

/**
 * One property as it appears on `GenericSelector`'s (`@cimpress-technology/generic-selector`)
 * resolved `state.product.properties` — itself sourced from
 * `@cimpress-technology/attribute-model-explorer`'s `IProduct`/`IProductProperty`.
 *
 * A locally-defined structural type rather than an import of that package's `IProductProperty` —
 * that package only re-exports the `IProduct` envelope from its public entry point, not the named
 * property/option/assignment interfaces. A local type covering just the fields read here keeps
 * this module's dependency on "does this JSON shape still match" (covered by this module's own
 * tests, using a real product fixture) rather than an internal file path in that package.
 */
export type ProductPropertyType = {
    name: string;
    type?: string;
    unitOfMeasure?: string;
    staticAssignment?: LiteralValueType;
    assignments?: PropertyAssignmentType[];
};

/** One option as it appears on `state.product.options` — only used here to read the list of valid
 * `Size` values for this product. */
export type ProductOptionType = {
    name: string;
    values?: LiteralValueType[];
};

/** Minimal shape of `GenericSelector`'s resolved `state.product` this module reads. */
export type GenericSelectorProductType = {
    options?: ProductOptionType[];
    properties?: ProductPropertyType[];
};

/** Whether `condition` is satisfied by `selections` — `selections` is whatever the current
 * `merchantVariables`-style selection map has for that attribute, plus (when resolving one size
 * chart cell) the specific size being resolved for `Size` itself. Returns `false` (not satisfied)
 * when the condition's attribute isn't present in `selections` at all — an unresolved condition
 * can never be treated as matched. */
function conditionIsSatisfied(condition: ConditionType, selections: Record<string, string | undefined>): boolean {
    const selectedValue = selections[condition.attribute];
    if (selectedValue === undefined) {
        return false;
    }

    const isListed = (condition.values ?? []).some((entry) => entry.value === selectedValue);
    return condition.operator === 'notIn' ? !isListed : isListed;
}

/**
 * Resolves one property's value for a given selection. `staticAssignment` (a property with one
 * fixed value regardless of selection — e.g. a "one size fits all" garment's `Width`) always wins
 * when present; otherwise the first assignment whose conditions **all** match `selections` wins.
 */
export function resolvePropertyValue(
    property: ProductPropertyType,
    selections: Record<string, string | undefined>,
): string | undefined {
    if (property.staticAssignment) {
        return property.staticAssignment.value;
    }

    const match = (property.assignments ?? []).find((assignment) =>
        (assignment.conditions ?? []).every((condition) => conditionIsSatisfied(condition, selections)),
    );
    return match?.result.value;
}

/** Every `Size` value this product declares, in declared order. Empty for a product with no
 * `Size` option at all — not necessarily "not apparel": a "one size fits all" garment may have no
 * `Size` option either, and still have a `Width`/`Length` worth showing (see
 * `buildSizeChartFromProduct`'s single-column fallback for that case). */
function getSizes(product: GenericSelectorProductType): string[] {
    const sizeOption = product.options?.find((option) => option.name === 'Size');
    return (sizeOption?.values ?? [])
        .map((entry) => entry.value)
        .filter((value): value is string => value !== undefined);
}

/** One name-matching rule: `pattern`/`excludes` are lowercase substrings checked against a
 * property's `name`; `multiplier` scales a resolved value (used for "Half Width" needing to be
 * doubled to get a full body width — not read by anything in this module today, since
 * `findMatchingProperty`/`buildSizeChartFromProduct` just display whatever matched as-is; kept on
 * the type since it's part of the patterns as ported from `auto-scene` and is exported for
 * Phase B's own value-resolving use — see `BODY_WIDTH_PATTERNS`). Exported, alongside
 * `BODY_WIDTH_PATTERNS`/`BODY_LENGTH_PATTERNS`, so Phase B's auto-placement work can resolve a
 * single inches value for the current size from the same two pattern lists this module's table
 * uses, without duplicating them. */
export type MeasurementPatternType = { pattern: string; excludes: string[]; multiplier: number };

/**
 * Priority-ordered candidate names for a garment's body width, ported verbatim from `auto-scene`'s
 * `analyzers/apparel.py::get_body_width_inches` (attribute naming varies across brands/vendors —
 * `"Width (B)"`, `"Body Width"`, `"Half Width"`, etc.).
 */
export const BODY_WIDTH_PATTERNS: MeasurementPatternType[] = [
    { pattern: 'width (b)', excludes: [], multiplier: 1 },
    { pattern: 'body width', excludes: [], multiplier: 1 },
    { pattern: 'product width', excludes: [], multiplier: 1 },
    { pattern: 'chest width', excludes: [], multiplier: 1 },
    { pattern: 'bust width', excludes: [], multiplier: 1 },
    { pattern: 'waist width', excludes: [], multiplier: 1 },
    // Ported from auto-scene as just `excludes: ['half']` — 'decoration' added here because this
    // module scans a product's *entire* property list, which can also contain
    // "Decoration Location N Width" — a print-area dimension, not a body measurement, that this
    // generic fallback would otherwise match by literal substring.
    { pattern: 'width', excludes: ['half', 'decoration'], multiplier: 1 },
    { pattern: 'half body width', excludes: [], multiplier: 2 },
    { pattern: 'half width', excludes: [], multiplier: 2 },
    { pattern: 'chest', excludes: ['half'], multiplier: 1 },
];

/**
 * Priority-ordered candidate names for a garment's body/product length, ported verbatim from
 * `auto-scene`'s `analyzers/apparel.py::get_body_length_inches`.
 */
export const BODY_LENGTH_PATTERNS: MeasurementPatternType[] = [
    { pattern: 'length (a)', excludes: [], multiplier: 1 },
    { pattern: 'body length', excludes: [], multiplier: 1 },
    { pattern: 'product length', excludes: [], multiplier: 1 },
    { pattern: 'inseam length', excludes: [], multiplier: 1 },
    { pattern: 'inseam', excludes: [], multiplier: 1 },
    // 'decoration' excluded for the same reason as BODY_WIDTH_PATTERNS' generic fallback — guards
    // against a future "Decoration Location N Length"-style property, even though today's known
    // decoration-area properties are Width/Height, not Length.
    { pattern: 'length', excludes: ['sleeve', 'decoration'], multiplier: 1 },
];

/** First property (in `patterns` priority order) whose `name` matches, or `undefined` if none do.
 * Pure name matching, no value resolution — used for the size chart table, which shows whatever a
 * matched property's data says as-is, including any cell that can't currently resolve. */
function findMatchingProperty(
    properties: ProductPropertyType[],
    patterns: readonly MeasurementPatternType[],
): ProductPropertyType | undefined {
    for (const { pattern, excludes } of patterns) {
        const match = properties.find((property) => {
            const nameLower = property.name.toLowerCase();
            return nameLower.includes(pattern) && !excludes.some((exclude) => nameLower.includes(exclude));
        });
        if (match) {
            return match;
        }
    }
    return undefined;
}

/**
 * Builds the Size Chart table's rows: one per matched Width/Length property, one column per
 * declared `Size` (or a single implicit column when the product has no `Size` option at all — a
 * "one size fits all" garment may still have a `Width`/`Length` worth showing, via
 * `staticAssignment`, with nothing to vary a column by).
 *
 * `selections` should be the current `merchantVariables`-style map for every attribute other than
 * `Size` — `Size` itself is overridden per column internally, since the table's whole point is
 * showing every size at once.
 *
 * Returns `null` when `product` is absent (e.g. this product is on the legacy/serialized
 * attribute-model path, which has no per-size structure to read at all — see the summary doc's
 * "Known gap" note) or neither Width nor Length has a matching property with any resolvable value.
 */
export function buildSizeChartFromProduct(
    product: GenericSelectorProductType | undefined | null,
    selections: Record<string, string | undefined>,
): SizeChartResultType | null {
    if (!product?.properties) {
        return null;
    }

    const sizes = getSizes(product);
    const columns = sizes.length > 0 ? sizes : [''];

    const matches = [
        findMatchingProperty(product.properties, BODY_WIDTH_PATTERNS),
        findMatchingProperty(product.properties, BODY_LENGTH_PATTERNS),
    ].filter((property): property is ProductPropertyType => !!property);

    const attributes: SizeChartAttributeType[] = matches
        .map((property) => {
            const values: Record<string, string> = {};
            columns.forEach((size) => {
                const value = resolvePropertyValue(property, { ...selections, Size: size });
                if (value !== undefined) {
                    values[size] = value;
                }
            });
            return { attribute: property.name, unit: property.unitOfMeasure ?? '', values };
        })
        .filter((attribute) => Object.keys(attribute.values).length > 0);

    return attributes.length > 0 ? { attributes } : null;
}

/** Converts `rawValue` (as it appears in a resolved property/size-chart-cell value — a plain
 * number, no embedded unit suffix) to inches using `unit`. Unrecognized/absent units are assumed
 * to already be inches. Exported for Phase B's own inches-resolving use, alongside
 * `resolvePropertyValue`/`BODY_WIDTH_PATTERNS`/`BODY_LENGTH_PATTERNS`. */
export function toInches(rawValue: string, unit: string | undefined): number | undefined {
    const value = parseFloat(rawValue);
    if (Number.isNaN(value)) {
        return undefined;
    }

    switch ((unit ?? '').toLowerCase()) {
        case 'cm':
            return value / 2.54;
        case 'mm':
            return value / 25.4;
        default:
            return value;
    }
}

/** Parses one already-built `SizeChartAttributeType` cell into inches, for `decorationSpecs.tsx`'s
 * mm-equivalent display alongside the native value. */
export function getSizeAttributeInches(attribute: SizeChartAttributeType, size: string): number | undefined {
    const raw = attribute.values[size];
    if (raw === undefined) {
        return undefined;
    }
    return toInches(raw, attribute.unit);
}

// Phase B's auto-placement geometry math (see
// ai-config/user-stories/apparel-positioning-and-auto-placement/summary.md) needs a single
// resolved-to-inches body Width/Length value for the current size. Not implemented here — nothing
// in this MR calls it yet, and an exported function with no caller anywhere in the app except its
// own tests shouldn't ship. Everything it needs is exported above: `resolvePropertyValue`,
// `toInches`, `BODY_WIDTH_PATTERNS`, `BODY_LENGTH_PATTERNS`, `MeasurementPatternType`,
// `GenericSelectorProductType`. The resolver itself should try each pattern in priority order and,
// within each pattern, every matching-named property before moving on — a matching-named property
// with no usable value for the given size must not stop the search, matching `auto-scene`'s own
// `get_body_width_inches`/`get_body_length_inches` fallthrough behavior exactly (this differs from
// `findMatchingProperty` above, which only returns the first name match with no such fallthrough —
// that's the right behavior for the table, which shows whatever matched as-is, but not for a
// resolver that needs an actual usable number).
